
import { useState, useEffect, useCallback } from 'react';
import { watchedEpisodesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface WatchedEpisode {
  id: string;
  user_id: string;
  drama_id: number;
  episode_number: number;
  watched_at: string;
  progress_seconds: number;
  completed: boolean;
}

export const useWatchedEpisodes = (dramaId: number) => {
  const { user } = useAuth();
  const [watchedEpisodes, setWatchedEpisodes] = useState<WatchedEpisode[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWatchedEpisodes = useCallback(async () => {
    if (!user || !dramaId) {
      setWatchedEpisodes([]);
      setLoading(false);
      return;
    }

    try {
      const response = await watchedEpisodesApi.getWatchedEpisodes(dramaId);

      if (response.success) {
        setWatchedEpisodes(response.data || []);
      } else {
        console.error('Error fetching watched episodes:', response.error);
      }
    } catch (error) {
      console.error('Error fetching watched episodes:', error);
    } finally {
      setLoading(false);
    }
  }, [user, dramaId]);

  useEffect(() => {
    fetchWatchedEpisodes();
  }, [fetchWatchedEpisodes]);

  const markAsWatched = useCallback(async (episodeNumber: number, completed: boolean = true) => {
    if (!user) return false;

    try {
      const response = await watchedEpisodesApi.markAsWatched(dramaId, episodeNumber, completed);

      if (!response.success) {
        console.error('Error marking episode as watched:', response.error);
        return false;
      }

      await fetchWatchedEpisodes();
      return true;
    } catch (error) {
      console.error('Error marking episode as watched:', error);
      return false;
    }
  }, [user, dramaId, fetchWatchedEpisodes]);

  const markAsUnwatched = useCallback(async (episodeNumber: number) => {
    if (!user) return false;

    try {
      const response = await watchedEpisodesApi.markAsUnwatched(dramaId, episodeNumber);

      if (!response.success) {
        console.error('Error removing watched episode:', response.error);
        return false;
      }

      await fetchWatchedEpisodes();
      return true;
    } catch (error) {
      console.error('Error removing watched episode:', error);
      return false;
    }
  }, [user, dramaId, fetchWatchedEpisodes]);

  const isEpisodeWatched = useCallback((episodeNumber: number) => {
    return watchedEpisodes.some(ep => ep.episode_number === episodeNumber && ep.completed);
  }, [watchedEpisodes]);

  const getWatchProgress = useCallback(() => {
    const completedCount = watchedEpisodes.filter(ep => ep.completed).length;
    return completedCount;
  }, [watchedEpisodes]);

  const markAllAsWatched = useCallback(async (totalEpisodes: number) => {
    if (!user) return false;

    try {
      const response = await watchedEpisodesApi.markAllAsWatched(dramaId, totalEpisodes);

      if (!response.success) {
        console.error('Error marking all episodes as watched:', response.error);
        return false;
      }

      await fetchWatchedEpisodes();
      return true;
    } catch (error) {
      console.error('Error marking all episodes as watched:', error);
      return false;
    }
  }, [user, dramaId, fetchWatchedEpisodes]);

  const clearAllWatched = useCallback(async () => {
    if (!user) return false;

    try {
      const response = await watchedEpisodesApi.clearAllWatched(dramaId);

      if (!response.success) {
        console.error('Error clearing watched episodes:', response.error);
        return false;
      }

      await fetchWatchedEpisodes();
      return true;
    } catch (error) {
      console.error('Error clearing watched episodes:', error);
      return false;
    }
  }, [user, dramaId, fetchWatchedEpisodes]);

  return {
    watchedEpisodes,
    loading,
    markAsWatched,
    markAsUnwatched,
    isEpisodeWatched,
    getWatchProgress,
    markAllAsWatched,
    clearAllWatched,
  };
};
